import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useNavigate } from 'react-router-dom'
import { Lock, Search, Trash2 } from 'lucide-react'
import { collectionsApi } from '../services/collectionsApi'
import { useMergeSelection } from '../hooks/useMergeSelection'
import { useCollectionHealth } from '../hooks/useCollectionHealth'
import CollectionActionsMenu from '../components/collections/CollectionActionsMenu'
import RoundPlusButton from '../components/RoundPlusButton'
import DuplicateReview from '../components/collections/DuplicateReview'
import MergeSelectionBar from '../components/collections/MergeSelectionBar'
import MergeReviewModal from '../components/collections/MergeReviewModal'
import { formatMergeSummary, formatPruneSummary } from '../components/collections/cleanupSummaries'
import { useNotify } from '../components/notice/useNotify'
import MergeCheckbox from '../components/collections/MergeCheckbox'
import CreateCollectionItemModal from '../components/collections/CreateCollectionItemModal'
import EditCollectionItemModal from '../components/collections/EditCollectionItemModal'
import EditCollectionModal from '../components/collections/EditCollectionModal'
import PersonCard from '../components/people/PersonCard'
import RankedRail, { RANKED_MAX_COUNT } from '../components/people/RankedRail'
import PersonDetailModal, {
    PersonDetailValue,
} from '../components/checkin/PersonDetailModal'
import {
    useRankedPeople,
    type RankedPersonItem,
} from '../hooks/useRankedPeople'
import { formatPersonDisplayName } from '../utils/people'
import type {
    CollectionWithItems,
    CollectionItem,
} from '../types/collections'

/**
 * Custom view for the People system collection. The generic table view (used
 * for arbitrary collections) renders names + sparse contact fields in a
 * cramped grid; people prefer to scan faces. This page replaces it with:
 *
 *  - An avatar grid (picture or initials) of every Person.
 *  - A horizontal pill bar to filter by `relation` (most-frequent first;
 *    overflow into a "More ▾" popover when there are more than 5 distinct
 *    relations).
 *  - An A-Z rail along the right edge that scrolls to the first card whose
 *    sort-letter starts with that letter. Letters with no matching card are
 *    rendered as inert spacers so the rail stays a stable visual anchor.
 *
 * Sort order: by `last name` if present, else `first name` (`§5.2` of
 * past_work/collections/PEOPLE_PLAN.md). The same key is used to derive the alpha section.
 *
 * Tap a card → `PersonDetailModal` with `onEdit` wired (the page is the
 * source-of-truth view, not a reference, so the Edit affordance applies —
 * `feedback_review_vs_edit` only excludes Edit on chip-tap viewer modals).
 * Edit hands off to the generic `EditCollectionItemModal`; no custom editor
 * since the canonical schema only uses field types the generic editor
 * already supports.
 */

const ALPHA_LETTERS: string[] = Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ')
const RELATION_PILL_LIMIT = 5

interface PeopleCollectionPageProps {
    collection: CollectionWithItems
    /** Admin viewers get a read-only schema affordance — the same
     *  EditCollectionModal as for user collections. Schema writes still 403
     *  on the backend. */
    isAdmin?: boolean
    /** Refetch trigger so the parent reloads after add/edit/delete. */
    onCollectionRefresh?: () => Promise<void> | void
}

const PeopleCollectionPage: React.FC<PeopleCollectionPageProps> = ({
    collection,
    isAdmin = false,
    onCollectionRefresh,
}) => {
    const navigate = useNavigate()
    const [filterRelation, setFilterRelation] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [viewing, setViewing] = useState<CollectionItem | null>(null)
    // Same viewer, reached from the duplicate review. Tracked separately so it
    // can open WITHOUT an edit path — from a comparison you are reading, not
    // editing, and an Edit button there would navigate away mid-decision.
    const [viewingReadOnly, setViewingReadOnly] = useState<CollectionItem | null>(null)
    const [editing, setEditing] = useState<CollectionItem | null>(null)
    const [creating, setCreating] = useState(false)
    const [editingCollection, setEditingCollection] = useState(false)
    const [deleteCandidate, setDeleteCandidate] = useState<CollectionItem | null>(null)
    const [deleteRefCount, setDeleteRefCount] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Entity-merge (de-duplication) multi-select. Selection survives search /
    // relation-filter changes and opening/closing the peek — see
    // useMergeSelection. Stage 6 wires `handleMergeClick` to the review modal.
    const merge = useMergeSelection()
    const [mergeReviewIds, setMergeReviewIds] = useState<number[] | null>(null)
    const notify = useNotify()
    const handleMergeClick = () => {
        setMergeReviewIds([...merge.selected])
    }

    // Prune-unreferenced multi-select (sibling of merge; uncapped, min 1). Lists
    // only zero-live-reference people, all pre-checked for deletion. Mutually
    // exclusive with merge — entering one exits the other.
    const prune = useMergeSelection({ min: 1, max: Infinity })
    // Counts for the two buttons below, so the page says whether either
    // would find anything before it is pressed. Refreshed after a prune or
    // a merge; a rename that makes or breaks a collision shows next load.
    const { health, refresh: refreshHealth } = useCollectionHealth(collection.id)
    // The grouped review behind the collision badge. Its own mode, exclusive
    // with merge and prune the way those two already are with each other.
    const [reviewingDuplicates, setReviewingDuplicates] = useState(false)
    const [pruneCandidateIds, setPruneCandidateIds] = useState<Set<number> | null>(null)
    const [pruneConfirmOpen, setPruneConfirmOpen] = useState(false)
    const [pruning, setPruning] = useState(false)
    const selectMode = merge.mergeMode || prune.mergeMode
    const activeSelection = prune.mergeMode ? prune : merge

    // Ranked rails. Fetched once on mount via the shared hook; the data is
    // derived from checkin_features tags on the server, so adding a Person
    // here (which has zero tags) doesn't change either ranking — no need to
    // refetch on the collection's refresh trigger.
    const { ranked } = useRankedPeople(RANKED_MAX_COUNT)

    const items = collection.items || []

    // Sort by (last name || first name), fall back to nickname / display name.
    // Locale-aware compare so e.g. accented letters group with their ASCII
    // counterparts on the alpha rail.
    const sorted = useMemo(() => {
        const decorated = items.map((it) => {
            const last = trimOrEmpty(it.data?.['last name'])
            const first = trimOrEmpty(it.data?.['first name'])
            const nickname = trimOrEmpty(it.data?.nickname)
            const sortKey = (last || first || nickname || '').toLowerCase()
            return { it, sortKey }
        })
        decorated.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        return decorated.map((d) => d.it)
    }, [items])

    // Distinct relations + counts. Sorted by frequency desc so the most-used
    // relations land in the visible pills before the overflow.
    const relationCounts = useMemo(() => {
        const counts = new Map<string, number>()
        for (const it of items) {
            const r = trimOrEmpty(it.data?.relation)
            if (!r) continue
            counts.set(r, (counts.get(r) || 0) + 1)
        }
        return [...counts.entries()].sort((a, b) => b[1] - a[1])
    }, [items])

    const visibleRelations = relationCounts.slice(0, RELATION_PILL_LIMIT)
    const overflowRelations = relationCounts.slice(RELATION_PILL_LIMIT)

    // Narrow by relation (AND) and the typeahead query (AND). Search matches
    // the concatenation of first/last/nickname so "Jane S" finds "Jane Smith"
    // even though no single field contains "Jane S".
    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase()
        return sorted.filter((it) => {
            // In prune mode, restrict to the zero-reference candidate set.
            if (prune.mergeMode && pruneCandidateIds && !pruneCandidateIds.has(it.id))
                return false
            if (
                filterRelation !== null &&
                trimOrEmpty(it.data?.relation) !== filterRelation
            )
                return false
            if (!q) return true
            const first = trimOrEmpty(it.data?.['first name']).toLowerCase()
            const last = trimOrEmpty(it.data?.['last name']).toLowerCase()
            const nickname = trimOrEmpty(it.data?.nickname).toLowerCase()
            const haystack = `${first} ${last} ${nickname}`.trim()
            return haystack.includes(q)
        })
    }, [sorted, filterRelation, searchQuery, prune.mergeMode, pruneCandidateIds])

    // Group by alpha letter, derived from the same key used for sorting.
    // Non-letter starts collapse into '#'.
    const sections = useMemo(() => {
        const map = new Map<string, CollectionItem[]>()
        for (const it of filtered) {
            map.set(itemLetter(it), (map.get(itemLetter(it)) || []).concat(it))
        }
        return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
    }, [filtered])

    const presentLetters = useMemo(() => new Set(sections.map(([l]) => l)), [sections])

    // Apply the same search + relation filter the alpha grid uses, so the
    // ranked rails stay coherent with what's visible below. Identity is the
    // collection_item_id, since the rails carry a leaner shape than the
    // alpha grid's CollectionItem rows.
    const rankedFilter = useMemo(() => {
        const q = searchQuery.trim().toLowerCase()
        return (rows: RankedPersonItem[]): RankedPersonItem[] =>
            rows.filter((p) => {
                if (
                    filterRelation !== null &&
                    trimOrEmpty(p.data?.relation) !== filterRelation
                )
                    return false
                if (!q) return true
                const first = trimOrEmpty(p.data?.['first name']).toLowerCase()
                const last = trimOrEmpty(p.data?.['last name']).toLowerCase()
                const nickname = trimOrEmpty(p.data?.nickname).toLowerCase()
                const haystack = `${first} ${last} ${nickname}`.trim()
                return haystack.includes(q)
            })
    }, [filterRelation, searchQuery])

    // Resolve the corresponding CollectionItem for a ranked person so the
    // card's onView/onEdit/onDelete handlers route through the same modals
    // as the alphabetical grid. Falls back to a synthesized minimal item if
    // the alpha list hasn't loaded (shouldn't happen — both come from the
    // same user's People collection — but defensive against race).
    const itemById = useMemo(() => {
        const m = new Map<number, CollectionItem>()
        for (const it of items) m.set(it.id, it)
        return m
    }, [items])

    const resolveItem = (p: RankedPersonItem): CollectionItem => {
        const hit = itemById.get(p.collection_item_id)
        if (hit) return hit
        return { id: p.collection_item_id, data: p.data } as CollectionItem
    }

    const refresh = async () => {
        if (onCollectionRefresh) await onCollectionRefresh()
    }

    // --- Prune mode wiring -------------------------------------------------
    const exitPrune = () => {
        prune.exitMergeMode()
        setPruneCandidateIds(null)
    }
    const enterPrune = async () => {
        merge.exitMergeMode()
        const ids = items.map((it) => it.id)
        try {
            const counts = await collectionsApi.items.referenceCounts(collection.id, ids)
            const candidates = ids.filter((id) => (counts[id] ?? 0) === 0)
            setPruneCandidateIds(new Set(candidates))
            prune.selectAll(candidates)
            prune.enterMergeMode()
        } catch (err) {
            console.error('Failed to load reference counts for prune:', err)
        }
    }
    const toggleMerge = () => {
        if (merge.mergeMode) {
            merge.exitMergeMode()
            return
        }
        if (reviewingDuplicates) {
            setReviewingDuplicates(false)
            return
        }
        exitPrune()
        // When grouping has already found collisions, showing them beats opening
        // an empty multi-select and asking the user to find them by eye. With
        // none found there is nothing to show, so fall through to picking.
        //
        // Dismissed clusters count as something to show even when no LIVE one
        // is left: the review panel is the only route to Restore, so gating on
        // active clusters alone strands a collection that has ruled on all of
        // them — the misclick this was all supposed to stay recoverable from.
        if ((health?.duplicate_clusters.length ?? 0) > 0
            || (health?.dismissed_clusters.length ?? 0) > 0) {
            setReviewingDuplicates(true)
            return
        }
        merge.enterMergeMode()
    }
    const togglePrune = () => {
        if (prune.mergeMode) {
            exitPrune()
            return
        }
        // Third member of the same mutually-exclusive set.
        setReviewingDuplicates(false)
        void enterPrune()
    }
    const handlePruneConfirm = async () => {
        setPruning(true)
        try {
            const result = await collectionsApi.items.purgeUnreferenced(collection.id, [...prune.selected])
            exitPrune()
            setPruneConfirmOpen(false)
            await refresh()
            notify.success(formatPruneSummary(result.reaped_ids.length, result.skipped_ids.length))
        } catch (err) {
            console.error('Failed to purge unreferenced people:', err)
            void refreshHealth()
        } finally {
            setPruning(false)
        }
    }

    const startDelete = async (person: CollectionItem) => {
        setDeleteCandidate(person)
        setDeleteRefCount(null)
        try {
            const result = await collectionsApi.items.delete(collection.id, person.id)
            setDeleteRefCount(
                result?.can_delete === false ? result.reference_count || 0 : 0,
            )
        } catch (err) {
            console.error('Reference check failed:', err)
            setDeleteRefCount(0)
        }
    }

    const handleDelete = async () => {
        if (!deleteCandidate) return
        setDeleting(true)
        try {
            // Confirm step always passes force=true. The earlier probe (no
            // force) only reads the reference count; the deletion itself is
            // gated on force regardless of whether references exist.
            await collectionsApi.items.delete(collection.id, deleteCandidate.id, true)
            setDeleteCandidate(null)
            setDeleteRefCount(null)
            await refresh()
        } catch (err) {
            console.error('Failed to delete person:', err)
        } finally {
            setDeleting(false)
        }
    }

    const scrollToLetter = (letter: string) => {
        const el = document.getElementById(`person-section-${letter}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <div className="min-h-screen bg-blush-light">
            <div className="max-w-7xl mx-auto px-4 pb-8 sm:pt-8">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                    <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate max-w-full">
                                    {collection.name}
                                </h1>
                                <span
                                    className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded"
                                    title="The structure of this collection is fixed. Honeyguide also adds people automatically as you mention them on checkins."
                                >
                                    <Lock className="w-3 h-3" />
                                    Fixed structure
                                </span>
                            </div>
                            {collection.description && (
                                <p className="text-gray-600 mt-1">{collection.description}</p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <CollectionActionsMenu
                                onEdit={isAdmin ? () => setEditingCollection(true) : undefined}
                                merge={items.length > 0 ? {
                                    active: merge.mergeMode || reviewingDuplicates,
                                    count: health?.duplicate_clusters.length,
                                    onToggle: toggleMerge,
                                } : undefined}
                                prune={items.length > 0 ? {
                                    active: prune.mergeMode,
                                    count: health?.orphan_count,
                                    onToggle: togglePrune,
                                } : undefined}
                            />
                            <RoundPlusButton onClick={() => setCreating(true)} label="Add person" />
                        </div>
                    </div>
                </div>

                {reviewingDuplicates && health && (
                    <DuplicateReview
                        collectionId={collection.id}
                        clusters={health.duplicate_clusters}
                        dismissedClusters={health.dismissed_clusters}
                        items={items}
                        onMerge={(ids) => setMergeReviewIds(ids)}
                        onChanged={refreshHealth}
                        onManualSelect={() => {
                            setReviewingDuplicates(false)
                            merge.enterMergeMode()
                        }}
                        onOpenDetail={(id) => {
                            const hit = items.find((it) => it.id === id)
                            if (hit) setViewingReadOnly(hit)
                        }}
                        onDone={() => setReviewingDuplicates(false)}
                    />
                )}

                {/* Toolbar — typeahead search + relation filter pills.
                    Search comes first because it's the more direct find-one-
                    person tool; relations are a coarser categorical filter
                    layered AND on top. */}
                {items.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4 space-y-3">
                        <PersonSearchInput
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                        {relationCounts.length > 0 && (
                            <RelationPills
                                value={filterRelation}
                                onChange={setFilterRelation}
                                allCount={items.length}
                                visible={visibleRelations}
                                overflow={overflowRelations}
                            />
                        )}
                    </div>
                )}

                {/* Ranked rails — Recent (last N days) and Closest (lifetime),
                    both ordered by COUNT(DISTINCT cf.checkin_id). Filter pulses
                    track the search + relation pill identically to the alpha
                    grid; a rail with no matches under the current filter is
                    hidden outright (header included). Each rail is collapsible
                    independently. */}
                {items.length > 0 && ranked && !selectMode && (
                    <div className="space-y-3 mb-4">
                        <RankedRail
                            title="Recent"
                            subtitle={`Last ${ranked.recent_days} days`}
                            people={rankedFilter(ranked.recent)}
                            onView={(p) => setViewing(resolveItem(p))}
                            onEdit={(p) => setEditing(resolveItem(p))}
                            onCheckins={(p) =>
                                navigate(`/checkins?collection_item=${p.collection_item_id}`)
                            }
                            onDelete={(p) => startDelete(resolveItem(p))}
                        />
                        <RankedRail
                            title="Closest"
                            subtitle="All time"
                            people={rankedFilter(ranked.lifetime)}
                            onView={(p) => setViewing(resolveItem(p))}
                            onEdit={(p) => setEditing(resolveItem(p))}
                            onCheckins={(p) =>
                                navigate(`/checkins?collection_item=${p.collection_item_id}`)
                            }
                            onDelete={(p) => startDelete(resolveItem(p))}
                        />
                    </div>
                )}

                {/* Body — alpha rail + grid */}
                {items.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
                        No people yet. Mention someone on a checkin or use Add Person.
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
                        No people match your filters.
                    </div>
                ) : (
                    <div className="flex gap-3">
                        <div className="flex-1 min-w-0 space-y-6">
                            {sections.map(([letter, group]) => (
                                <section
                                    key={letter}
                                    id={`person-section-${letter}`}
                                    className="scroll-mt-20"
                                >
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sticky top-0 bg-gray-50 py-1">
                                        {letter}
                                    </h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {group.map((person) => (
                                            <div key={person.id} className="relative">
                                                {selectMode && (
                                                    <div className="absolute top-2 left-2 z-10">
                                                        <MergeCheckbox
                                                            checked={activeSelection.isSelected(person.id)}
                                                            onToggle={() => activeSelection.toggle(person.id)}
                                                        />
                                                    </div>
                                                )}
                                                <div
                                                    className={
                                                        selectMode && activeSelection.isSelected(person.id)
                                                            ? 'ring-2 ring-action rounded-lg'
                                                            : undefined
                                                    }
                                                >
                                                    <PersonCard
                                                        person={person}
                                                        onView={() => setViewing(person)}
                                                        onEdit={() => setEditing(person)}
                                                        onCheckins={() =>
                                                            navigate(`/checkins?collection_item=${person.id}`)
                                                        }
                                                        onDelete={() => startDelete(person)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>

                        {/* Alpha rail (right edge). Letters not present in
                            the current sections are inert + faded; tapping a
                            present letter scrolls its section into view. */}
                        <AlphaRail
                            present={presentLetters}
                            onPick={scrollToLetter}
                        />
                    </div>
                )}
            </div>


                        {/* Merge selection bar — floats while in merge mode. "Merge" is a
                Stage-6 hand-off; here it only surfaces count + enable state. */}
            <MergeSelectionBar
                visible={merge.mergeMode}
                count={merge.count}
                onMergeClick={handleMergeClick}
                onClear={merge.clear}
                onCancel={merge.exitMergeMode}
            />

            {/* Prune selection bar — the red "Delete N" sibling of the merge bar. */}
            <MergeSelectionBar
                visible={prune.mergeMode}
                count={prune.count}
                variant="prune"
                actionLabel="Delete"
                actionIcon={Trash2}
                minCount={1}
                maxCount={Infinity}
                hintText={{ empty: 'Select items to delete', belowMin: 'Select at least 1' }}
                onMergeClick={() => setPruneConfirmOpen(true)}
                onClear={prune.clear}
                onCancel={exitPrune}
            />

            {pruneConfirmOpen && (
                <PruneConfirm
                    count={prune.count}
                    deleting={pruning}
                    onCancel={() => setPruneConfirmOpen(false)}
                    onConfirm={handlePruneConfirm}
                />
            )}

            {mergeReviewIds && (
                <MergeReviewModal
                    collection={collection}
                    itemIds={mergeReviewIds}
                    onClose={() => setMergeReviewIds(null)}
                    onMerged={async (summary) => {
                        setMergeReviewIds(null)
                        merge.exitMergeMode()
                        await refresh()
                        void refreshHealth()
                        notify.success(
                            formatMergeSummary(
                                summary.repointed_checkin_count,
                                summary.reaped_loser_ids.length
                            )
                        )
                    }}
                />
            )}

            {/* Detail modal — onEdit wired so the user can hop into the
                generic edit modal directly from the viewer. */}
            <PersonDetailModal
                open={viewing !== null}
                value={viewing ? toDetailValue(viewing) : null}
                onEdit={viewing ? () => {
                    const p = viewing
                    setViewing(null)
                    setEditing(p)
                } : undefined}
                onClose={() => setViewing(null)}
            />

            {/* The same viewer, read-only: no `onEdit`, per the component's own
                contract ("read-only browsing surfaces omit it"). */}
            <PersonDetailModal
                open={viewingReadOnly !== null}
                value={viewingReadOnly ? toDetailValue(viewingReadOnly) : null}
                onClose={() => setViewingReadOnly(null)}
            />

            {creating && (
                <CreateCollectionItemModal
                    collection={collection}
                    onClose={() => setCreating(false)}
                    onSuccess={async () => {
                        setCreating(false)
                        await refresh()
                    }}
                />
            )}

            {editing && (
                <EditCollectionItemModal
                    collection={collection}
                    item={editing as any}
                    onClose={() => setEditing(null)}
                    onSuccess={async () => {
                        setEditing(null)
                        await refresh()
                    }}
                />
            )}

            {deleteCandidate && (
                <DeleteConfirm
                    person={deleteCandidate}
                    refCount={deleteRefCount}
                    deleting={deleting}
                    onCancel={() => {
                        setDeleteCandidate(null)
                        setDeleteRefCount(null)
                    }}
                    onConfirm={handleDelete}
                />
            )}

            {editingCollection && (
                <EditCollectionModal
                    collection={collection}
                    onClose={() => setEditingCollection(false)}
                    onSuccess={async () => {
                        setEditingCollection(false)
                        await refresh()
                    }}
                />
            )}
        </div>
    )
}

// ------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------

function trimOrEmpty(value: unknown): string {
    if (typeof value !== 'string') return ''
    return value.trim()
}

function itemLetter(item: CollectionItem): string {
    const last = trimOrEmpty(item.data?.['last name'])
    const first = trimOrEmpty(item.data?.['first name'])
    const nickname = trimOrEmpty(item.data?.nickname)
    const source = (last || first || nickname || '').toUpperCase()
    if (!source) return '#'
    const ch = source[0]
    return /[A-Z]/.test(ch) ? ch : '#'
}

function toDetailValue(item: CollectionItem): PersonDetailValue {
    return { collectionItemId: item.id, data: item.data || {} }
}

// ------------------------------------------------------------------------
// Typeahead search input
// ------------------------------------------------------------------------

const PersonSearchInput: React.FC<{
    value: string
    onChange: (v: string) => void
}> = ({ value, onChange }) => (
    <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Escape' && value) {
                    e.stopPropagation()
                    onChange('')
                }
            }}
            placeholder="Search people by name or nickname…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
        <AnimatePresence>
            {value && (
                <motion.button
                    type="button"
                    onClick={() => onChange('')}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    whileHover={{ scale: 1.15, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                    aria-label="Clear search"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </motion.button>
            )}
        </AnimatePresence>
    </div>
)

// ------------------------------------------------------------------------
// Toolbar pills
// ------------------------------------------------------------------------

const RelationPills: React.FC<{
    value: string | null
    onChange: (v: string | null) => void
    allCount: number
    visible: [string, number][]
    overflow: [string, number][]
}> = ({ value, onChange, allCount, visible, overflow }) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!menuOpen) return
        const onDocMouseDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', onDocMouseDown)
        return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [menuOpen])

    const Pill: React.FC<{ active: boolean; onClick: () => void; label: string; count: number }> = ({
        active, onClick, label, count,
    }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                active
                    ? 'bg-sky-600 text-white border-sky-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
        >
            {label}
            <span className={`ml-1.5 text-xs ${active ? 'text-sky-100' : 'text-gray-500'}`}>
                {count}
            </span>
        </button>
    )

    // Selected overflow relation: show it inline (replaces the trailing "More")
    // so the active filter is always visible.
    const selectedFromOverflow = value && !visible.some(([r]) => r === value)
        ? value
        : null
    const selectedOverflowCount =
        selectedFromOverflow != null
            ? overflow.find(([r]) => r === selectedFromOverflow)?.[1] ?? 0
            : 0

    return (
        <div className="flex flex-wrap items-center gap-2">
            <Pill
                active={value === null}
                onClick={() => onChange(null)}
                label="All"
                count={allCount}
            />
            {visible.map(([rel, count]) => (
                <Pill
                    key={rel}
                    active={value === rel}
                    onClick={() => onChange(rel)}
                    label={rel}
                    count={count}
                />
            ))}
            {selectedFromOverflow && (
                <Pill
                    key={selectedFromOverflow}
                    active
                    onClick={() => onChange(null)}
                    label={selectedFromOverflow}
                    count={selectedOverflowCount}
                />
            )}
            {overflow.length > 0 && (
                <div className="relative" ref={menuRef}>
                    <button
                        type="button"
                        onClick={() => setMenuOpen((v) => !v)}
                        className="px-3 py-1 text-sm rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    >
                        More ▾
                    </button>
                    {menuOpen && (
                        <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded shadow-md w-48 max-h-72 overflow-y-auto text-sm">
                            {overflow.map(([rel, count]) => (
                                <button
                                    key={rel}
                                    type="button"
                                    onClick={() => {
                                        onChange(rel)
                                        setMenuOpen(false)
                                    }}
                                    className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 flex justify-between ${
                                        value === rel ? 'text-sky-700 font-medium' : 'text-gray-700'
                                    }`}
                                >
                                    <span className="truncate">{rel}</span>
                                    <span className="text-xs text-gray-500 ml-2">{count}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// ------------------------------------------------------------------------
// Alpha rail
// ------------------------------------------------------------------------

const AlphaRail: React.FC<{
    present: Set<string>
    onPick: (letter: string) => void
}> = ({ present, onPick }) => {
    return (
        <div className="hidden sm:flex flex-col items-center gap-0.5 sticky top-20 self-start py-2 text-xs select-none">
            {ALPHA_LETTERS.map((letter) => {
                const enabled = present.has(letter)
                return (
                    <button
                        key={letter}
                        type="button"
                        disabled={!enabled}
                        onClick={() => onPick(letter)}
                        className={`w-5 h-4 leading-none flex items-center justify-center rounded ${
                            enabled
                                ? 'text-sky-600 hover:bg-sky-100 cursor-pointer'
                                : 'text-gray-300 cursor-default'
                        }`}
                    >
                        {letter}
                    </button>
                )
            })}
            {present.has('#') && (
                <button
                    type="button"
                    onClick={() => onPick('#')}
                    className="w-5 h-4 leading-none flex items-center justify-center rounded text-sky-600 hover:bg-sky-100 cursor-pointer"
                >
                    #
                </button>
            )}
        </div>
    )
}

// ------------------------------------------------------------------------
// Delete confirmation
// ------------------------------------------------------------------------

const DeleteConfirm: React.FC<{
    person: CollectionItem
    refCount: number | null
    deleting: boolean
    onCancel: () => void
    onConfirm: () => void
}> = ({ person, refCount, deleting, onCancel, onConfirm }) => {
    const name = formatPersonDisplayName(person.data || {})
    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget && !deleting) onCancel()
            }}
        >
            <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-2xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Delete person</h3>
                {refCount == null ? (
                    <div className="flex items-center text-gray-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2" />
                        Checking for references…
                    </div>
                ) : (
                    <>
                        <p className="text-gray-700 mb-3">
                            Are you sure you want to delete "<span className="font-medium">{name}</span>"?
                        </p>
                        {refCount > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                                <p className="text-yellow-800 text-sm">
                                    ⚠️ This person is referenced in {refCount} checkin{refCount === 1 ? '' : 's'}.
                                    Deleting will detach those references.
                                </p>
                            </div>
                        )}
                    </>
                )}
                <div className="flex justify-end gap-3 mt-3">
                    <button
                        onClick={onCancel}
                        disabled={deleting || refCount == null}
                        className="px-4 py-2 text-gray-700 bg-secondary hover:bg-secondary-dark rounded-md disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={deleting || refCount == null}
                        className="px-4 py-2 bg-danger hover:bg-danger-dark text-white rounded-md disabled:opacity-50 flex items-center"
                    >
                        {deleting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                Deleting…
                            </>
                        ) : refCount && refCount > 0 ? (
                            'Delete Anyway'
                        ) : (
                            'Delete'
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ------------------------------------------------------------------------
// Prune confirmation + toast copy
// ------------------------------------------------------------------------

const PruneConfirm: React.FC<{
    count: number
    deleting: boolean
    onCancel: () => void
    onConfirm: () => void
}> = ({ count, deleting, onCancel, onConfirm }) => (
    <div
        className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
            if (e.target === e.currentTarget && !deleting) onCancel()
        }}
    >
        <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Prune unreferenced items</h3>
            <p className="text-gray-700 mb-4">
                Delete {count} unreferenced item{count === 1 ? '' : 's'}? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
                <button
                    onClick={onCancel}
                    disabled={deleting}
                    className="px-4 py-2 text-gray-700 bg-secondary hover:bg-secondary-dark rounded-md disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    disabled={deleting}
                    className="px-4 py-2 bg-danger hover:bg-danger-dark text-white rounded-md disabled:opacity-50 flex items-center"
                >
                    {deleting ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Deleting…
                        </>
                    ) : (
                        'Delete'
                    )}
                </button>
            </div>
        </div>
    </div>
)

export default PeopleCollectionPage
